import { type DatasourceResponseSchemaProperty } from '@atlaskit/linking-types';
import { type DefaultRootState, type defaultStoreUtils } from '@databases/redux';
import { type SimpleStore } from '@databases/redux-types';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import {
  BacklinkDisplayType,
  type DatabaseField,
  DatabaseFieldType
} from '@databases/types/database';
import { DatabaseCalculationType } from '@databases/types/database/view';
import { DatabaseChangeType } from '@databases/types/redux';
import {
  getStringRepresentation,
  isEmptyValue
} from '@databases/utils/database/field-types';
import { getReferencedJiraIssueDetailsField } from '@databases/utils/database/field-types/jiraIssueDetails';
import {
  getDatabaseEntryRequired,
  getDatabaseFieldRequired,
  getDatabaseValue
} from '@databases/utils/database/utils';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { loadSupportedFields } from '@databases/utils/redux/datasources/actions';
import { selectSupportedProperties } from '@databases/utils/redux/datasources/selectors';

import { type CalculationSelectOption } from './types.ts';

export const isEmpty = (
  contentId: string,
  fieldId: string,
  entryId: string,
  store: SimpleStore<DefaultRootState>
): boolean => {
  const state = store.getState();
  const database = selectDatabaseByContentIdRequired(state, { contentId });
  const field = getDatabaseFieldRequired(database, fieldId);

  const entry = getDatabaseEntryRequired(database, entryId);
  const value = getDatabaseValue(database, field, entry);

  // For checkbox fields, treat unchecked (false) as empty for quick calc purposes.
  // This is separate from the field-type-level isEmptyValue which always returns false
  // for checkbox (to avoid hiding unchecked checkboxes in card view).
  if (
    field.type === DatabaseFieldType.CHECKBOX &&
    value.type === DatabaseFieldType.CHECKBOX
  ) {
    return !value.value;
  }

  if (field.type === value.type) {
    return isEmptyValue({ database, field, entry, value }, state);
  } else {
    // The entry does not always equal the field type (users can change the
    // field type). In this case, it is possible the value can be converted to
    // the field type and displayed. If this is the case, it is not empty. To
    // determine if it has a display value, we get the string representation.
    return !getStringRepresentation({ database, field, entry, value }, state);
  }
};

export const getUniqueIdentifiers = (
  databaseContentId: string,
  fieldId: string,
  entryId: string,
  store: SimpleStore<DefaultRootState>
) => {
  const state = store.getState();
  const database = selectDatabaseByContentIdRequired(state, {
    contentId: databaseContentId
  });

  const field = getDatabaseFieldRequired(database, fieldId);
  const entry = getDatabaseEntryRequired(database, entryId);
  const value = getDatabaseValue(database, field, entry);

  // TODO: retrieve `stringRepresentation` from caller's `entry` param instead of
  // selecting from store
  const stringRepresentation = getStringRepresentation(
    { database, field, entry, value },
    state
  );

  if (!stringRepresentation?.trim()) {
    return [];
  }

  if (field.type === DatabaseFieldType.PAGE_LINK) {
    const aris: string[] = [];
    if (Array.isArray(value.value)) {
      for (const v of value.value) {
        if (typeof v !== 'string' && 'ari' in v && v.ari !== undefined) {
          aris.push(v.ari);
        }
      }
    }

    if (aris.length > 0) {
      return aris;
    }
  }

  if (field.type === DatabaseFieldType.TEXT) {
    return [stringRepresentation];
  }

  if (
    field.type === DatabaseFieldType.ENTRY_LINK &&
    field.settings.contentId === undefined
  ) {
    return [];
  }

  if (
    field.type === DatabaseFieldType.BACKLINK &&
    field.settings.backlinkDisplayType === BacklinkDisplayType.LINK
  ) {
    return stringRepresentation.split('\n,');
  }

  return stringRepresentation.trim().split('\n');
};

const numericOptions = [
  DatabaseCalculationType.SUM,
  DatabaseCalculationType.AVERAGE,
  DatabaseCalculationType.MIN,
  DatabaseCalculationType.MAX,
  DatabaseCalculationType.COUNT_TOTAL,
  DatabaseCalculationType.COUNT,
  DatabaseCalculationType.COUNT_UNIQUE,
  DatabaseCalculationType.COUNT_EMPTY,
  DatabaseCalculationType.PERCENT_EMPTY,
  DatabaseCalculationType.PERCENT_NOT_EMPTY
];

const nonNumericOptions = [
  DatabaseCalculationType.COUNT_TOTAL,
  DatabaseCalculationType.COUNT,
  DatabaseCalculationType.COUNT_UNIQUE,
  DatabaseCalculationType.COUNT_EMPTY,
  DatabaseCalculationType.PERCENT_EMPTY,
  DatabaseCalculationType.PERCENT_NOT_EMPTY
];

const checkboxOptions = [
  DatabaseCalculationType.COUNT_TOTAL,
  DatabaseCalculationType.COUNT,
  DatabaseCalculationType.COUNT_EMPTY,
  DatabaseCalculationType.PERCENT_NOT_EMPTY,
  DatabaseCalculationType.PERCENT_EMPTY
];

const isJiraFieldNumeric = (
  jiraFieldKey: string | undefined,
  jiraFieldProperties: DatasourceResponseSchemaProperty[] | undefined
): boolean => {
  if (!jiraFieldKey || !jiraFieldProperties || jiraFieldProperties.length === 0) {
    return false;
  }

  const jiraField = jiraFieldProperties.find((prop) => prop.key === jiraFieldKey);
  return jiraField?.type === 'number';
};

export const getCalculationOptionsForFieldType = (
  field: DatabaseField<DatabaseFieldType>,
  state?: DefaultRootState,
  contentId?: string,
  dispatch?: ReturnType<typeof defaultStoreUtils.useDispatch>
): DatabaseCalculationType[] => {
  switch (field.type) {
    case DatabaseFieldType.NUMBER:
      return numericOptions;
    case DatabaseFieldType.CHECKBOX:
      return checkboxOptions;
    case DatabaseFieldType.BACKLINK:
      if (field.settings.backlinkDisplayType === BacklinkDisplayType.NUMBER) {
        return numericOptions;
      } else {
        return nonNumericOptions;
      }
    case DatabaseFieldType.ATTACHMENT:
      return nonNumericOptions.filter(
        (calculation) => calculation !== DatabaseCalculationType.COUNT_UNIQUE
      );
    case DatabaseFieldType.JIRA_ISSUE_DETAILS: {
      if (!state || !contentId) {
        return nonNumericOptions;
      }

      const database = selectDatabaseByContentIdRequired(state, { contentId });
      const referencedJiraField = getReferencedJiraIssueDetailsField({ database, field });
      const cloudId = referencedJiraField?.settings.cloudId;

      if (!cloudId) {
        return nonNumericOptions;
      }

      const jiraFieldProperties = selectSupportedProperties(state, cloudId, 'jira');

      // If properties haven't been loaded yet, trigger a load and default to non-numeric options
      if (!jiraFieldProperties || jiraFieldProperties.length === 0) {
        if (dispatch) {
          dispatch(loadSupportedFields(cloudId, 'jira'));
        }
        return nonNumericOptions;
      }

      if (isJiraFieldNumeric(field.settings.jiraField, jiraFieldProperties)) {
        return numericOptions;
      } else {
        return nonNumericOptions;
      }
    }
    default:
      return nonNumericOptions;
  }
};

export const handleCalculationSelectionChange = (
  option: CalculationSelectOption | null,
  databaseContentId: string,
  dispatch: ReturnType<typeof defaultStoreUtils.useDispatch>,
  fieldId: string,
  viewId: string
) => {
  if (option) {
    dispatch(
      addChangeToDraft(databaseContentId, {
        type: DatabaseChangeType.SET_SAVED_VIEW_CALCULATIONS,
        viewId: viewId,
        fieldId: fieldId,
        value: option.value
      })
    );
  } else {
    dispatch(
      addChangeToDraft(databaseContentId, {
        type: DatabaseChangeType.REMOVE_SAVED_VIEW_CALCULATION,
        viewId: viewId,
        fieldId: fieldId
      })
    );
  }
};

export const isPercentCalculation = (calculationType: DatabaseCalculationType) => {
  return (
    calculationType === DatabaseCalculationType.PERCENT_EMPTY ||
    calculationType === DatabaseCalculationType.PERCENT_NOT_EMPTY
  );
};

export const calculationRequiresFieldValues = (
  calculationType: DatabaseCalculationType
) => {
  return ![DatabaseCalculationType.COUNT_TOTAL].includes(calculationType);
};

export const isQuickCalcDisabledFieldTypes = (fieldType: DatabaseFieldType) => {
  return [
    DatabaseFieldType.PAGE_STATUS,
    DatabaseFieldType.PAGE_LABELS,
    DatabaseFieldType.PAGE_DETAILS
  ].includes(fieldType);
};

// Adds a percent sign to the end of the result if the calculation type is a percent calculation.
export const formatPercent = (
  calculationType: DatabaseCalculationType,
  result: string
) => {
  if (isPercentCalculation(calculationType) && !result.endsWith('%')) {
    return `${result}%`;
  } else {
    return result;
  }
};

// keep != null check for width as width value can be nullish (0)
export const getGridTemplateColumns = (fieldWidths: (number | undefined)[]) =>
  fieldWidths?.map((width) => (width != null ? `${width}px` : '1fr')).join(' ');
